/*
  ea-progress.js  ·  Eva-Alert
  Extends Pebble's <progress-timeline> gradient fill to the LAST step.

  WHY: each segment's colour height is var(--progress), and the ONLY thing that turns
  scroll position into --progress is Pebble's core loop in theme.js — which is hard-coded
  to skip the final block (`if (!block.nextElementSibling) continue`). So the bar always
  dies at the last pill. This re-runs the IDENTICAL reading-line formula (refY = viewport
  mid-height; t = how far refY has passed through the block) for the last step only, so it
  draws in exactly like steps 1-2 and reaches the end of the last subtext. All visuals stay
  in ea-progress.css; this file only supplies --progress for the step core skips.

  Self-contained, no deps, no Lenis access needed: an IntersectionObserver gates a rAF
  sampler, so it tracks native OR smooth (Lenis) scrolling frame-for-frame, and only while
  the section is on screen. Guarded against double-boot; re-boots on shopify:section:load.

  Matches core exactly:
   - host rect  = the <progress-timeline> element (same element core measures as `this`)
   - viewport   = window.innerHeight (read each frame, so resize is free)
   - half-screen early-out gates are copied verbatim from theme.js #updateTimelineProgress
   - on leave: freeze the last value (core also stops updating when out of view)
*/
(function () {
  if (window.__eaProgressLastInit) return;
  window.__eaProgressLastInit = true;

  var active = new Set();
  var rafId = null;

  function lastProgressEl(section) {
    var blocks = section.querySelectorAll('.timeline-block');
    if (blocks.length < 2) return null; // a single step is hidden natively (only-child)
    return blocks[blocks.length - 1].querySelector('.timeline-block__progress');
  }

  function update(section) {
    var el = lastProgressEl(section);
    if (!el) return;
    var block = el.closest('.timeline-block');
    if (!block) return;

    var viewportH = window.innerHeight;
    var halfScreen = viewportH * 0.5;

    var hostRect = section.getBoundingClientRect();
    var visibleTop = Math.max(hostRect.top, 0);
    var visibleBottom = Math.min(hostRect.bottom, viewportH);
    var visibleHeight = Math.max(0, visibleBottom - visibleTop);
    var sectionTopPastViewport = hostRect.top < 0;

    // Same two early-out gates the core uses, so timing matches steps 1-2 exactly.
    if (visibleHeight <= 0) {
      el.style.setProperty('--progress', '0%');
      return;
    }
    if (!sectionTopPastViewport && visibleHeight < halfScreen) {
      el.style.setProperty('--progress', '0%');
      return;
    }

    var refY = halfScreen;
    var rect = block.getBoundingClientRect();
    var h = rect.height;
    if (h <= 0) return;

    var t;
    if (refY <= rect.top) {
      t = 0;
    } else if (refY >= rect.bottom) {
      t = 1;
    } else {
      t = (refY - rect.top) / h;
    }

    el.style.setProperty('--progress', (Math.round(t * 1000) / 10) + '%');
  }

  function frame() {
    rafId = null;
    active.forEach(update);
    if (active.size) rafId = requestAnimationFrame(frame);
  }

  function pump() {
    if (!rafId && active.size) rafId = requestAnimationFrame(frame);
  }

  function boot(section) {
    if (section.__eaProgressBound) return;
    section.__eaProgressBound = true;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          active.add(section);
          pump();
        } else {
          active.delete(section); // freeze the last value, mirroring core's on-leave
        }
      });
    }, { rootMargin: '100px' });

    io.observe(section);
  }

  function init(root) {
    (root || document).querySelectorAll('progress-timeline').forEach(boot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme-editor re-render: re-scan the reloaded section.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
